import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, User, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API, getAuthHeaders } from '../utils/api';

export default function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, formData, { withCredentials: true });
      
      toast.success('Account created successfully!');
      navigate('/dashboard', { state: { user: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#060A10] flex">
      {/* Left side - Image/branding */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1611974765270-ca12586343bb?q=80&w=2070&auto=format&fit=crop)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/80 to-blue-900/80 backdrop-blur-sm"></div>
        <div className="relative h-full flex items-center justify-center p-12">
          <div className="text-center">
            <h3 className="text-4xl font-bold font-['Manrope'] mb-4">Join the Elite Trading Community</h3>
            <p className="text-xl text-slate-200">Start making data-driven trading decisions today</p>
          </div>
        </div>
      </div>

      {/* Right side - Signup form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="flex items-center gap-2 justify-center mb-6">
              <TrendingUp className="w-10 h-10 text-blue-500" />
              <span className="text-3xl font-bold font-['Manrope']">TradeForge</span>
            </div>
            <h2 className="text-4xl font-bold font-['Manrope'] text-center mb-2">Create Account</h2>
            <p className="text-center text-slate-400">Get started with your trading journey</p>
          </div>

          <div className="bg-[#121214] border border-zinc-800 rounded-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                <div className="mt-2 relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    data-testid="signup-name-input"
                    className="pl-10 bg-zinc-950 border-zinc-800 focus:ring-2 focus:ring-blue-600 focus:border-transparent text-white h-11"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <div className="mt-2 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    data-testid="signup-email-input"
                    className="pl-10 bg-zinc-950 border-zinc-800 focus:ring-2 focus:ring-blue-600 focus:border-transparent text-white h-11"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="mt-2 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    data-testid="signup-password-input"
                    className="pl-10 bg-zinc-950 border-zinc-800 focus:ring-2 focus:ring-blue-600 focus:border-transparent text-white h-11"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="signup-submit-btn"
                className="w-full bg-blue-600 hover:bg-blue-700 h-11 shadow-[0_0_15px_rgba(59,130,246,0.3)] transition-all duration-200"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
          </div>

          <p className="text-center text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-500 hover:text-blue-400 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
